import { FilterSetType, Gender, PositionGroup, UserConfigDefinition } from '@statsbomb/parachute-types';
import { atomWithMutation } from 'jotai-tanstack-query';
import { userConfigIdAtom } from '@/atoms/userConfigs';
import { MAX_SELECTED_METRICS, MIN_SELECTED_METRICS } from '@/consts/radar';
import { CONFIG_NAME_MAX_LENGTH, PLACEHOLDER_SETTINGS_CONFIG_ID } from '@/consts/userConfigs';
import { RadarDataFormat } from '@/types/radar';
import { TableId } from '@/types/table';
import {
  UserConfigFilter,
  OnboardingItemKey,
  UserConfigPutMutationParams,
  UserConfigType,
  RadarTemplateDefinition,
  UserConfigSettingsDefinition,
} from '@/types/userConfigs';
import { isNullish, isValidEnumValue } from '@/utils/predicates';
import { fetchClientAtom } from '../../queries/client';

export const isValidUserConfigDefinition = ({
  configType,
  configDefinition,
}: {
  configType: UserConfigType;
  configDefinition: UserConfigDefinition;
}): boolean => {
  if (isValidEnumValue(FilterSetType, configType)) {
    const { filters } = configDefinition as UserConfigFilter;
    return filters !== undefined;
  }

  if (configType === UserConfigType.SETTINGS) {
    const { team, gender, radar } = configDefinition as UserConfigSettingsDefinition;
    const isValidTeam =
      !('team' in configDefinition) || (!!team && typeof team.datahubId === 'number' && typeof team.iqId === 'number');
    const isValidGender = !('gender' in configDefinition) || isValidEnumValue(Gender, gender);
    const isValidRadarConfig = !('radar' in configDefinition) || isValidEnumValue(RadarDataFormat, radar?.dataFormat);

    return isValidTeam && isValidGender && isValidRadarConfig;
  }

  if (configType === UserConfigType.TABLE_COLUMNS) {
    const { tableId, selectedColumns } = configDefinition;
    return tableId !== undefined && isValidEnumValue(TableId, tableId) && !!selectedColumns.length;
  }

  if (configType === UserConfigType.ONBOARDING) {
    return Object.keys(configDefinition).every(
      key => isValidEnumValue(OnboardingItemKey, key) && typeof configDefinition[key] === 'boolean',
    );
  }

  if (configType === UserConfigType.RADAR_TEMPLATE) {
    const { entity, positions, positionGroup, metrics, titleSettings } = configDefinition as RadarTemplateDefinition;
    const validName =
      typeof titleSettings.title === 'string' &&
      !!titleSettings.title &&
      titleSettings.title.length <= CONFIG_NAME_MAX_LENGTH;

    const validPositions = isNullish(positions) || (Array.isArray(positions) && positions.length > 0);
    const validPositionGroup =
      entity === 'player' ? isValidEnumValue(PositionGroup, positionGroup) : positionGroup === null;

    const validMetrics =
      Array.isArray(metrics) &&
      metrics.every(
        metric =>
          !!metric.key &&
          !!metric.keyNormalised &&
          typeof metric.key === 'string' &&
          typeof metric.keyNormalised === 'string' &&
          typeof metric.min === 'number' &&
          typeof metric.max === 'number' &&
          typeof metric.default === 'boolean' &&
          metric.max > metric.min,
      );

    const validMetricAmount = metrics.length >= MIN_SELECTED_METRICS && metrics.length <= MAX_SELECTED_METRICS;

    return validPositions && validPositionGroup && validMetrics && validName && validMetricAmount;
  }

  return false;
};

export const upsertUserConfigMutation = async (
  fetch: (input: RequestInfo, init?: RequestInit) => Promise<any>,
  userConfigMutations: UserConfigPutMutationParams[],
) => {
  // hooray, I successfully did nothing
  if (!userConfigMutations.length) return { isSuccess: true, configIds: [] };

  const areAllDefinitionsValid = userConfigMutations.every(({ configType, configDefinition }) => {
    const isValid = isValidUserConfigDefinition({ configType, configDefinition });
    if (!isValid) {
      // eslint-disable-next-line no-console
      console.error(
        `Bad config definition provided for ${configType} config. ${JSON.stringify(configDefinition, null, 2)}`,
      );
    }
    return isValid;
  });

  if (!areAllDefinitionsValid) {
    return { isSuccess: false };
  }

  const itemDefinitions = userConfigMutations.map(({ configId, configType, configDefinition }) => ({
    config_id: configId && configId !== PLACEHOLDER_SETTINGS_CONFIG_ID ? configId : crypto.randomUUID(),
    type: configType,
    definition: configDefinition,
  }));

  const configIds = itemDefinitions.map(({ config_id: configId }) => configId);

  try {
    await fetch('/user/configs', {
      method: 'PUT',
      body: JSON.stringify(itemDefinitions),
      headers: {
        'Content-type': 'application/json',
      },
    });
  } catch {
    return {
      configIds,
      isSuccess: false,
    };
  }

  return {
    configIds,
    isSuccess: true,
  };
};

export const upsertUserConfigAtom = atomWithMutation(get => {
  const { fetch } = get(fetchClientAtom);
  const mutationKey = ['upsertUserConfig'];

  const mutationFn = async (userConfigMutations: UserConfigPutMutationParams[]) =>
    upsertUserConfigMutation(fetch, userConfigMutations);

  return { mutationFn, mutationKey };
});

export const deleteUserConfigAtom = atomWithMutation(get => {
  const { fetch } = get(fetchClientAtom);
  const mutationKey = ['deleteUserConfig'];
  const mutationFn = async (configId: string) => {
    try {
      await fetch(`/user/config/${configId}`, {
        method: 'DELETE',
      });
    } catch {
      return { isSuccess: false };
    }

    return { isSuccess: true };
  };

  return {
    mutationFn,
    mutationKey,
  };
});

export const upsertUserConfigPermissionsAtom = atomWithMutation(get => {
  const { fetch } = get(fetchClientAtom);
  const userConfigId = get(userConfigIdAtom);
  const mutationKey = ['upsertUserConfigPermissions'];

  const mutationFn = async (permissions: { hudl_user_id: string; permission: 'view' | 'edit' | 'full' }[]) => {
    if (!userConfigId) return { isSuccess: false };

    try {
      await fetch(`/user/config/${userConfigId}/permissions`, {
        method: 'PUT',
        body: JSON.stringify(permissions),
        headers: { 'Content-type': 'application/json' },
      });
    } catch {
      return { isSuccess: false };
    }

    return { isSuccess: true };
  };

  return { mutationFn, mutationKey };
});
